// The customer account: sign in, sign up, forget a password, and read back the two things an
// account holds — the prints bought and the designs made.
//
// Every call here goes out with `credentials: "include"`, because the session is an HttpOnly cookie
// rather than a token this code could hold. That is deliberate: a token in localStorage is readable
// by any script that gets onto the page, and a cookie the browser will not hand to script is not.
// It also means the browser, not this file, decides when the session is over.

import { API_BASE_URL, ApiError } from "@/lib/api";

export type Account = {
  id: string;
  email: string;
  name?: string | null;
  /** False until they click the link we mailed. Past orders are only claimed once this is true. */
  emailVerified: boolean;
  /** False on an account that has only ever signed in with Google or Facebook. */
  hasPassword: boolean;
  /** The social logins linked to this account, e.g. ["google"]. */
  providers: string[];
  createdAt: string;
};

export type AccountOrderItem = {
  sku: string;
  productName: string;
  price: number;
  quantity: number;
};

export type AccountOrder = {
  id: string;
  orderNumber: string;
  status: "pending" | "paid" | "processing" | "shipped" | "completed" | "cancelled" | "failed";
  createdAt: string;
  paidAt?: string | null;
  amount: number;
  currency: string;
  imageJobId: string;
  imageUrl?: string | null;
  carName?: string | null;
  styleSlug: string;
  items: AccountOrderItem[];
  trackingUrl?: string | null;
  trackingNumber?: string | null;
  carrier?: string | null;
  trackingStatus?: string | null;
  deliveredAt?: string | null;
  /** We are holding the print, waiting for them to approve their proof. The one state that needs them. */
  awaitingArtworkApproval: boolean;
};

export type AuthProviders = {
  google: boolean;
  facebook: boolean;
};

export type OAuthProvider = "google" | "facebook";

async function accountRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/api/account${path}`, {
    ...init,
    cache: "no-store",
    // The whole reason this file exists separately from lib/api.ts.
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    let code: string | null = null;
    let message = `Request failed with status ${response.status}`;
    try {
      const parsed = JSON.parse(body) as { error?: string; message?: string };
      if (typeof parsed.error === "string") code = parsed.error;
      if (typeof parsed.message === "string") message = parsed.message;
      else if (code) message = code;
    } catch {
      // Not JSON — keep the status message.
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return undefined as T;
  return (await response.json()) as T;
}

/** The signed-in account, or null. Never throws for "signed out" — that is the normal case. */
export async function fetchAccount(): Promise<Account | null> {
  try {
    return (await accountRequest<Account | undefined>("/me")) ?? null;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return null;
    throw error;
  }
}

export async function registerAccount(input: {
  email: string;
  password: string;
  name?: string;
  locale: string;
  designIds?: string[];
}): Promise<Account> {
  return accountRequest<Account>("/register", { method: "POST", body: JSON.stringify(input) });
}

export async function signIn(input: {
  email: string;
  password: string;
  designIds?: string[];
}): Promise<Account> {
  return accountRequest<Account>("/login", { method: "POST", body: JSON.stringify(input) });
}

export async function signOut(): Promise<void> {
  await accountRequest<void>("/logout", { method: "POST" });
}

/** Always resolves, whether or not that address has an account — see the backend for why. */
export async function requestPasswordReset(email: string, locale: string): Promise<void> {
  await accountRequest<void>("/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email, locale }),
  });
}

export async function resetPassword(token: string, password: string): Promise<Account> {
  return accountRequest<Account>("/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export async function verifyEmail(token: string): Promise<Account> {
  return accountRequest<Account>("/verify-email", { method: "POST", body: JSON.stringify({ token }) });
}

export async function resendVerification(locale: string): Promise<void> {
  await accountRequest<void>("/resend-verification", {
    method: "POST",
    body: JSON.stringify({ locale }),
  });
}

export async function fetchAccountOrders(): Promise<AccountOrder[]> {
  return (await accountRequest<AccountOrder[]>("/orders")) ?? [];
}

/** Same shape as the JobSummary the designs page already renders. */
export async function fetchAccountDesigns(): Promise<import("@/lib/api").JobSummary[]> {
  return (await accountRequest<import("@/lib/api").JobSummary[]>("/designs")) ?? [];
}

/** Hands this browser's remembered design ids to the account. Only unowned designs move. */
export async function claimDesigns(jobIds: string[]): Promise<number> {
  if (jobIds.length === 0) return 0;
  const result = await accountRequest<{ claimed: number }>("/designs/claim", {
    method: "POST",
    body: JSON.stringify({ jobIds }),
  });
  return result?.claimed ?? 0;
}

/**
 * Hides a design in the account's list, or shows it again. The poster and any order printed from
 * it are untouched — it only moves between the main list and the "Hidden" section.
 */
export async function setAccountDesignHidden(jobId: string, hidden: boolean): Promise<void> {
  await accountRequest<void>(`/designs/${jobId}/hidden`, {
    method: "PUT",
    body: JSON.stringify({ hidden }),
  });
}

export async function fetchAuthProviders(): Promise<AuthProviders> {
  try {
    return (await accountRequest<AuthProviders>("/providers")) ?? { google: false, facebook: false };
  } catch {
    // A provider list we could not load means no social buttons, which is the safe way to be wrong.
    return { google: false, facebook: false };
  }
}

/**
 * Where the "Continue with Google/Facebook" button points. A full navigation, not a fetch: the
 * provider's consent screen is a page, and the backend needs to set its state cookie on the way out.
 */
export function oauthStartUrl(provider: OAuthProvider, locale: string, returnTo?: string): string {
  const params = new URLSearchParams({ locale });
  if (returnTo) params.set("returnTo", returnTo);
  return `${API_BASE_URL}/api/account/oauth/${provider}/start?${params.toString()}`;
}

/**
 * Turns a backend error code into the message key the account pages show. Anything unrecognised
 * falls through to a generic line rather than surfacing a raw code to the customer.
 */
export function accountErrorKey(error: unknown): string {
  const code = error instanceof ApiError ? error.code : null;
  switch (code) {
    case "invalid_credentials":
    case "email_taken":
    case "invalid_email":
    case "invalid_token":
    case "password_too_short":
    case "password_too_long":
    case "password_too_obvious":
    case "password_required":
    case "rate_limit_exceeded":
    case "account_locked":
      return code;
    default:
      return "generic";
  }
}

export type ReferralEarning = {
  earnedAt: string;
  amountUsd: number;
  friendEmailMasked: string;
};

export type AccountReferral = {
  code: string;
  url: string;
  creditUsd: number;
  earnedUsd: number;
  spentUsd: number;
  friendsReferred: number;
  earnings: ReferralEarning[];
};

/** This account's referral link and credit, or null when they have no code yet. */
export async function fetchAccountReferral(): Promise<AccountReferral | null> {
  return accountRequest<AccountReferral | null>("/api/account/referral");
}
